import type { Address } from '@getstep/proto/common'
import useMergedRef from '@react-hook/merged-ref'
import { createRef, forwardRef, memo, useEffect } from 'react'
import { usePlacesWidget } from 'react-google-autocomplete'

import type { TextInputProps } from '@components/input/text-input'
import { TextInput } from '@components/input/text-input'

/**
 * Address input with autocomplete from Google Places. Uncontrolled use only!
 */
export const AddressInput = memo(
    forwardRef<HTMLInputElement, Props>(
        ({ onChange, value, ...restOfProps }, ref) => {
            const innerRef = createRef<HTMLInputElement>()

            const widget = usePlacesWidget<HTMLInputElement>({
                apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
                inputAutocompleteValue: value,
                onPlaceSelected: ({ address_components: components }) => {
                    if (!components) return

                    const [street, number, city, state, zip] = (
                        ['street', 'number', 'city', 'state', 'zip'] as const
                    ).map((c) => components.find(config[c]))
                    onChange({
                        city: city && longName(city),
                        stateProvinceRegion: state && shortName(state),
                        line1:
                            street &&
                            (number
                                ? `${shortName(number)} ${shortName(street)}`
                                : shortName(street)),
                        zipPostalCode: zip && shortName(zip),
                    })
                },
                options: {
                    types: ['address'],
                    fields: ['address_components'],
                    componentRestrictions: { country: 'us' },
                },
            })

            // @ts-expect-error Target is not included in EventListener
            const uncontrolledOnChange: EventListener = (
                e: Event & { target: HTMLInputElement },
            ) => {
                const line1 = e.target.value
                if (line1) onChange({ line1 })
            }

            useEffect(() => {
                const input = innerRef.current
                if (!input) return
                // Making sure that the input value is updated,
                // Even if we didn't pick a suggestion
                input.addEventListener('change', uncontrolledOnChange)

                return () =>
                    input.removeEventListener('change', uncontrolledOnChange)
            }, [])

            const multiRef = useMergedRef(ref, widget.ref, innerRef)
            return (
                <TextInput {...restOfProps} ref={multiRef} autoComplete='off' />
            )
        },
    ),
)
interface Props extends Omit<TextInputProps, 'onChange'> {
    onChange: (
        address: Partial<
            Pick<
                Address,
                'stateProvinceRegion' | 'city' | 'line1' | 'zipPostalCode'
            >
        >,
    ) => void | Promise<void>
}

type AddressComponent = google.maps.GeocoderAddressComponent

const shortName = (c: AddressComponent) => c['short_name']
const longName = (c: AddressComponent) => c['long_name']

const config: Record<string, (c: AddressComponent) => boolean> = {
    street: ({ types }) => types.includes('route'),
    number: ({ types }) => types.includes('street_number'),
    city: ({ types }) => types.includes('locality'),
    state: ({ types }) => types.includes('administrative_area_level_1'),
    zip: ({ types }) => types.includes('postal_code'),
}
